import { OvFormInput, OvFormProvider } from '@components'
import { LABELS } from '@constants'
import { useStore } from '@hooks/useStore'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material'
import { ISingleProduct } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

interface ProductFormModalProps {
  open: boolean
  product?: ISingleProduct | null
  onClose: () => void
  onSave: (productData: Partial<ISingleProduct>) => Promise<void>
}

export const ProductFormModal = observer(({ open, product, onClose, onSave }: ProductFormModalProps) => {
  const { company } = useStore()

  const form = useForm<Partial<ISingleProduct>>({
    defaultValues: {
      name: '',
      description: '',
      unit_price: null,
      unit: '',
      sku: '',
      vat_code_id: null,
      is_active: true,
    },
  })

  const isCompanyTaxPayable = company.current_company?.tax_payable ?? true

  useEffect(() => {
    if (product) {
      form.reset({
        name: product.name,
        description: product.description,
        unit_price: product.unit_price,
        unit: product.unit,
        sku: product.sku,
        vat_code_id: product.vat_code_id,
        is_active: product.is_active,
      })
    } else {
      form.reset({
        name: '',
        description: '',
        unit_price: null,
        unit: '',
        sku: '',
        vat_code_id: null,
        is_active: true,
      })
    }
  }, [product, open, form])

  const onSubmitCallback = async (data: FieldValues) => {
    try {
      await onSave(data as Partial<ISingleProduct>)
      onClose()
    } catch (error) {
      console.error('Failed to save product:', error)
    }
  }

  const handleSubmit = () => {
    form.handleSubmit(
      onSubmitCallback,
      () => {
        toast('Vul alle verplichte velden in', { type: 'error' })
      }
    )()
  }

  const handleCancel = () => {
    onClose()
  }

  const handleFormProviderSubmit = () => {
    // Form submission is handled by handleSubmit button click
  }

  return (
    <OvFormProvider
      form={form}
      onSubmit={handleFormProviderSubmit}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth>
        <DialogTitle>{product ? 'Product bewerken' : 'Nieuw product'}</DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={2}
            sx={{ mt: 1 }}>
            <Grid
              item
              xs={12}>
              <OvFormInput
                name="name"
                label={LABELS.TITLE}
                required
              />
            </Grid>

            <Grid
              item
              xs={12}>
              <OvFormInput
                name="description"
                label={LABELS.EXTRA_DESCRIPTION}
                multiline
                rows={3}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}>
              <OvFormInput
                name="unit_price"
                type="currency"
                label={LABELS.UNIT_PRICE}
              />
            </Grid>

            {isCompanyTaxPayable && (
              <Grid
                item
                xs={12}
                md={6}>
                <OvFormInput
                  label={LABELS.VAT}
                  name="vat_code_id"
                  type="vat"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            disabled={form.formState.isSubmitting}>
            {LABELS.CANCEL}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Opslaan...' : LABELS.PRODUCT_SAVE}
          </Button>
        </DialogActions>
      </Dialog>
    </OvFormProvider>
  )
})
